import { useState, useRef, useCallback, useEffect } from "react";
import {
  sanitizeInput,
  sanitizeEmail,
  sanitizePhone,
} from "../../../utils/sanitization";
import { useAllEventManagement } from "./useAllEventManagement";
import {
  getTournamentAverageCatalog,
  listCatalogExtractedPeople,
} from "../../../utility/averageCatalogs";
import { searchOrgBowlersForTeamCreate } from "../../../utility/bowlerMatch";
import {
  normalizeUsbcId,
  resolveCatalogAverageForBowler,
  searchCatalogPeopleForTeamCreate,
} from "../../../utility/catalogAverageMatch";
import { reviewBowlersFromEntry } from "../../../utility/signupReview";

export const useBowlerManagement = (
  selectedEvent,
  existingBowlers,
  tournamentBowlers,
  allEventConfigs = [],
  tournamentID = null
) => {
  const [bowlers, setBowlers] = useState([]);
  const [catalogAssignment, setCatalogAssignment] = useState(null);
  const [catalogReady, setCatalogReady] = useState(!tournamentID);
  const searchTimeoutRef = useRef(null);
  const skipPaperRefillRef = useRef(false);
  const catalogPeopleRef = useRef([]);
  const minGamesRef = useRef(21);

  useEffect(() => {
    if (!tournamentID) {
      catalogPeopleRef.current = [];
      setCatalogAssignment(null);
      setCatalogReady(true);
      return;
    }
    let cancelled = false;
    setCatalogReady(false);
    (async () => {
      try {
        const assignment = await getTournamentAverageCatalog(tournamentID);
        if (cancelled) return;
        if (!assignment?.catalogID || !assignment.catalog?.organizationID) {
          catalogPeopleRef.current = [];
          setCatalogAssignment(null);
          return;
        }
        const people = await listCatalogExtractedPeople(
          assignment.catalogID,
          assignment.catalog.organizationID
        );
        if (cancelled) return;
        catalogPeopleRef.current = people;
        minGamesRef.current = assignment.minGamesForAverage;
        setCatalogAssignment(assignment);
      } catch (error) {
        console.error("Error loading tournament catalog:", error);
        if (!cancelled) {
          catalogPeopleRef.current = [];
          setCatalogAssignment(null);
        }
      } finally {
        if (!cancelled) setCatalogReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tournamentID]);

  const catalogSuggestionsFor = (rows, bowlerIndex, fields = {}) => {
    const row = { ...rows[bowlerIndex], ...fields };
    return searchCatalogPeopleForTeamCreate({
      query: row.name,
      usbcID: row.usbcID,
      email: row.email,
      phone: row.phone,
      writtenAverage: row.average,
      catalogPeople: catalogPeopleRef.current,
      minGamesForAverage: minGamesRef.current,
      excludeIds: rows
        .filter((_, index) => index !== bowlerIndex)
        .map((other) => other.catalogMatch?.extractedBowlerID)
        .filter(Boolean),
    });
  };

  const applyCatalogSuggestion = (bowler, suggestion) => {
    const next = {
      ...bowler,
      catalogMatch: suggestion,
      catalogSuggestions: [],
    };
    if (!bowler.existingBowler) {
      next.name = suggestion.name;
    }
    if (suggestion.average != null) {
      next.average = Math.round(Number(suggestion.average));
      next.hasAverage = true;
      next.averagePopulated = true;
    } else {
      next.average = "";
      next.hasAverage = false;
      next.averagePopulated = false;
    }
    if (suggestion.usbcId && !normalizeUsbcId(bowler.usbcID)) {
      next.usbcID = suggestion.usbcId;
    }
    return next;
  };

  // All event management hook
  const { loadBowlerAllEventStatus, autoSelectAllEventsForBowler, getAllEventStatus } =
    useAllEventManagement(tournamentID, allEventConfigs, selectedEvent, setBowlers);

  // Load all event status for bowlers when they're identified
  useEffect(() => {
    if (!tournamentID || !allEventConfigs.length) return;

    const loadAllEventStatusForBowlers = async () => {
      const bowlersWithIds = bowlers.filter(
        (b) => b.id && b.existingBowler && !b.allEventStatus
      );
      if (bowlersWithIds.length === 0) return;

      for (const bowler of bowlersWithIds) {
        try {
          const allEventStatus = await loadBowlerAllEventStatus(bowler.id);
          
          if (!allEventStatus) continue;

          // Find bowler index before state update
          const currentBowlers = bowlers;
          const bowlerIndex = currentBowlers.findIndex((b) => b.id === bowler.id);
          
          // Update bowler with all event status
          setBowlers((prev) => {
            const updated = [...prev];
            const index = updated.findIndex((b) => b.id === bowler.id);
            if (index >= 0) {
              updated[index] = {
                ...updated[index],
                allEventStatus,
              };
            }
            return updated;
          });
          
          // Trigger auto-selection after status is loaded (Case 3)
          // Use setTimeout to ensure state update completes first
          if (bowlerIndex >= 0) {
            setTimeout(() => {
              if (selectedEvent && selectedEvent.isPrimary) {
                autoSelectAllEventsForBowler(bowler.id, bowlerIndex);
              }
            }, 100);
          }
        } catch (error) {
          console.error(`Error loading all event status for bowler ${bowler.id}:`, error);
        }
      }
    };

    loadAllEventStatusForBowlers();
  }, [bowlers.map((b) => b.id).join(","), tournamentID, allEventConfigs.length, selectedEvent?.id, loadBowlerAllEventStatus, autoSelectAllEventsForBowler]);

  // Initialize bowlers when event changes
  const initializeBowlers = useCallback((quantity, options = {}) => {
    // Clear existing bowlers first, then set new ones
    setBowlers([]);

    const enterSingles = Boolean(options.enterSingles);
    // Use setTimeout to ensure the clear happens before the new bowlers are set
    setTimeout(() => {
      const newBowlers = Array.from({ length: quantity }, (_, index) => ({
        id: `temp-${Date.now()}-${index}`,
        name: "",
        average: "",
        usbcID: "",
        email: "",
        phone: "",
        existingBowler: false, // Default to new bowler
        hasAverage: true, // Default to having average (matches old wizard)
        averagePopulated: false, // Track if average was populated from database
        selectedSidePots: {},
        bracketTickets: {},
        eventEntries: [], // Initialize empty event entries array
        enterSingles,
        isLookupOpen: false,
        lookupResults: [],
        catalogSuggestions: [],
        catalogMatch: null,
        errors: {},
      }));
      setBowlers(newBowlers);
    }, 0);
  }, []);

  // Update bowler field (no sanitization on keystroke)
  const handleBowlerUpdate = useCallback((index, field, value) => {
    skipPaperRefillRef.current = true;
    setBowlers((prev) => {
      const updated = [...prev];
      const updatedBowler = {
        ...updated[index],
        [field]: value,
        errors: { ...updated[index].errors, [field]: null },
      };

      // If updating average field, also update hasAverage
      if (field === "average") {
        updatedBowler.hasAverage = value && value !== "" ? true : false;
        updatedBowler.averagePopulated = false;
        if (updatedBowler.catalogMatch) {
          const catalogAverage = updatedBowler.catalogMatch.average;
          const entered =
            value === "" || value == null ? null : Number(value);
          updatedBowler.averageDisagrees = Boolean(
            Number.isFinite(entered) &&
              catalogAverage != null &&
              Number(catalogAverage) !== entered
          );
        }
        if (!updatedBowler.existingBowler) {
          updatedBowler.existingBowler = false;
        }
      }

      // If updating hasAverage field, clear averagePopulated flag
      if (field === "hasAverage") {
        updatedBowler.averagePopulated = false;
        updatedBowler.catalogMatch = null;
      }

      if (field === "name") {
        if (
          updated[index].catalogMatch &&
          updatedBowler.averagePopulated
        ) {
          updatedBowler.average = "";
          updatedBowler.averagePopulated = false;
        }
        updatedBowler.catalogMatch = null;
      }

      updated[index] = updatedBowler;
      return updated;
    });
  }, []);

  // Handle bowler field blur (sanitize on blur)
  const handleBowlerBlur = useCallback((index, field, value) => {
    skipPaperRefillRef.current = true;
    setBowlers((prev) => {
      const updated = [...prev];

      // Sanitize input based on field type
      let sanitizedValue = value;
      switch (field) {
        case "name":
          sanitizedValue = sanitizeInput(value);
          break;
        case "email":
          sanitizedValue = sanitizeEmail(value);
          break;
        case "phone":
          sanitizedValue = sanitizePhone(value);
          break;
        case "usbcID":
          sanitizedValue = sanitizeInput(value);
          break;
        case "average":
          // For numbers, just trim whitespace
          sanitizedValue = value ? value.toString().trim() : "";
          break;
        default:
          sanitizedValue = value;
      }

      const updatedBowler = {
        ...updated[index],
        [field]: sanitizedValue,
        errors: { ...updated[index].errors, [field]: null },
      };

      // If updating average field, also update hasAverage
      if (field === "average") {
        updatedBowler.hasAverage =
          sanitizedValue && sanitizedValue !== "" ? true : false;
        // If manually entering average, mark as new bowler
        if (!updatedBowler.existingBowler) {
          updatedBowler.existingBowler = false;
        }
      }

      updated[index] = updatedBowler;

      if (["name", "usbcID", "email", "phone", "average"].includes(field)) {
        const suggestions = catalogSuggestionsFor(updated, index, {
          [field]: sanitizedValue,
        });
        const resolved = resolveCatalogAverageForBowler({
          currentBowler: updatedBowler,
          catalogSuggestions: suggestions,
        });
        updated[index] = {
          ...updatedBowler,
          ...resolved,
        };
      }

      return updated;
    });
  }, []);

  // Toggle side pot selection for a bowler
  const handleBowlerSidePotToggle = useCallback((bowlerIndex, eventId) => {
    setBowlers((prev) => {
      const updated = [...prev];
      const currentSidePots = updated[bowlerIndex].selectedSidePots || {};
      const newSidePots = {
        ...currentSidePots,
        [eventId]: !currentSidePots[eventId],
      };
      updated[bowlerIndex] = {
        ...updated[bowlerIndex],
        selectedSidePots: newSidePots,
      };
      return updated;
    });
  }, []);

  const handleBowlerTicketChange = useCallback(
    (bowlerIndex, productId, nextCount) => {
      setBowlers((prev) => {
        const updated = [...prev];
        updated[bowlerIndex] = {
          ...updated[bowlerIndex],
          bracketTickets: {
            ...(updated[bowlerIndex].bracketTickets || {}),
            [productId]: nextCount,
          },
        };
        return updated;
      });
    },
    []
  );

  const handleBowlerSinglesToggle = useCallback((bowlerIndex) => {
    skipPaperRefillRef.current = true;
    setBowlers((prev) => {
      const updated = [...prev];
      updated[bowlerIndex] = {
        ...updated[bowlerIndex],
        enterSingles: !updated[bowlerIndex].enterSingles,
      };
      return updated;
    });
  }, []);

  // Toggle lookup dropdown
  const handleLookupToggle = useCallback((bowlerIndex) => {
    setBowlers((prev) => {
      const updated = [...prev];
      updated[bowlerIndex] = {
        ...updated[bowlerIndex],
        isLookupOpen: !updated[bowlerIndex].isLookupOpen,
        lookupResults: [],
      };
      return updated;
    });
  }, []);

  // Handle lookup search with debouncing
  const handleLookupSearch = useCallback(
    (bowlerIndex, searchTerm) => {
      if (!searchTerm.trim()) {
        setBowlers((prev) => {
          const updated = [...prev];
          updated[bowlerIndex] = {
            ...updated[bowlerIndex],
            lookupResults: [],
            catalogSuggestions: updated[bowlerIndex].catalogMatch
              ? []
              : catalogSuggestionsFor(updated, bowlerIndex, { name: "" }),
          };
          return updated;
        });
        return;
      }

      // Clear existing timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Set new timeout for debounced search
      searchTimeoutRef.current = setTimeout(() => {
        // Merge existing bowlers with tournament bowlers (which have averages)
        // Deduplicate by bowler ID, preferring tournament bowlers (which have averages)
        const bowlerMap = new Map();

        // Add existing bowlers first
        existingBowlers.forEach((bowler) => {
          bowlerMap.set(bowler.id, bowler);
        });

        // Add tournament bowlers (these will override existing bowlers with averages)
        tournamentBowlers.forEach((bowler) => {
          bowlerMap.set(bowler.id, bowler);
        });

        const allBowlers = Array.from(bowlerMap.values());

        setBowlers((prev) => {
          const updated = [...prev];
          const current = updated[bowlerIndex];
          const lookupResults = searchOrgBowlersForTeamCreate({
            query: searchTerm,
            usbcID: current?.usbcID,
            email: current?.email,
            phone: current?.phone,
            writtenAverage: current?.average,
            orgBowlers: allBowlers,
          });
          updated[bowlerIndex] = {
            ...current,
            lookupResults,
            catalogSuggestions: current?.catalogMatch
              ? []
              : catalogSuggestionsFor(updated, bowlerIndex, {
                  name: searchTerm,
                }),
          };
          return updated;
        });
      }, 300); // 300ms debounce
    },
    [existingBowlers, tournamentBowlers]
  );


  // Handle bowler selection from lookup
  const handleLookupSelect = useCallback((bowlerIndex, bowler) => {
    skipPaperRefillRef.current = true;
    setBowlers((prev) => {
      const updated = [...prev];
      const current = updated[bowlerIndex] || {};
      const previousBowlerId = current.id;
      const newBowlerId = bowler.id;
      const bowlerIdChanged = previousBowlerId !== newBowlerId;
      const nextName = bowler.name || current.name;
      const nextUsbc = bowler.isNew
        ? current.usbcID || ""
        : bowler.usbcID || current.usbcID || "";
      const nextEmail = bowler.isNew
        ? current.email || ""
        : bowler.email || current.email || "";
      const nextPhone = bowler.isNew
        ? current.phone || ""
        : bowler.phone || current.phone || "";
      const suggestions = catalogSuggestionsFor(updated, bowlerIndex, {
        name: nextName,
        usbcID: nextUsbc,
        email: nextEmail,
        phone: nextPhone,
      });
      const resolved = resolveCatalogAverageForBowler({
        currentBowler: current,
        catalogSuggestions: suggestions,
        selectedOrgBowler: bowler,
      });

      updated[bowlerIndex] = {
        ...current,
        name: nextName,
        usbcID: nextUsbc,
        email: nextEmail,
        phone: nextPhone,
        existingBowler: bowler.isNew ? false : true,
        id: bowler.id,
        eventEntries: bowler.isNew
          ? current.eventEntries || []
          : bowler.eventEntries || [],
        isLookupOpen: false,
        lookupResults: [],
        errors: { ...current.errors, name: null },
        allEventStatus: bowlerIdChanged ? undefined : current.allEventStatus,
        selectedSidePots: current.selectedSidePots || {},
        bracketTickets: current.bracketTickets || {},
        previousAverage: resolved.average,
        previousHasAverage: resolved.hasAverage,
        ...resolved,
      };

      return updated;
    });
  }, []);

  const handleCatalogSelect = useCallback((bowlerIndex, suggestion) => {
    skipPaperRefillRef.current = true;
    setBowlers((prev) => {
      const updated = [...prev];
      updated[bowlerIndex] = applyCatalogSuggestion(
        updated[bowlerIndex],
        suggestion
      );
      return updated;
    });
  }, []);

  const handleCatalogClear = useCallback(
    (bowlerIndex) => {
      setBowlers((prev) => {
        const updated = [...prev];
        const current = updated[bowlerIndex];
        const hasStoredPrevious = current.previousHasAverage !== undefined;
        const restoredAverage = hasStoredPrevious
          ? current.previousAverage ?? ""
          : "";
        const restoredHasAverage = hasStoredPrevious
          ? current.previousHasAverage !== false
          : true;
        let average = restoredAverage;
        let hasAverage = restoredHasAverage;
        let averagePopulated = !!(hasAverage && average !== "" && average != null);
        if (!hasStoredPrevious && current.existingBowler && current.id) {
          const source = [
            ...(tournamentBowlers || []),
            ...(existingBowlers || []),
          ].find((row) => row.id === current.id);
          if (source) {
            average = source.average || "";
            hasAverage = source.hasAverage === false ? false : true;
            averagePopulated = !!(average && average !== "");
          }
        }
        updated[bowlerIndex] = {
          ...current,
          catalogMatch: null,
          hasAverage,
          average: hasAverage ? average : "",
          averagePopulated,
          catalogSuggestions: catalogSuggestionsFor(updated, bowlerIndex),
        };
        return updated;
      });
    },
    [existingBowlers, tournamentBowlers]
  );

  // Reset bowlers
  const resetBowlers = useCallback(() => {
    setBowlers([]);
  }, []);

  const applySignupReviewBowlers = useCallback(
    (entry, { teamSize, existingBowlers, tournamentBowlers, sidePotEvents, alwaysEnterSingles }) => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }
      skipPaperRefillRef.current = false;
      setBowlers(
        reviewBowlersFromEntry({
          entry,
          teamSize,
          existingBowlers,
          tournamentBowlers,
          catalogPeople: catalogPeopleRef.current,
          sidePotEvents,
          minGamesForAverage: minGamesRef.current,
          alwaysEnterSingles: Boolean(alwaysEnterSingles),
        })
      );
    },
    []
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return {
    bowlers,
    setBowlers,
    initializeBowlers,
    handleBowlerUpdate,
    handleBowlerBlur,
    handleBowlerSidePotToggle,
    handleBowlerTicketChange,
    handleBowlerSinglesToggle,
    handleLookupToggle,
    handleLookupSearch,
    handleLookupSelect,
    handleCatalogSelect,
    handleCatalogClear,
    catalogAssignment,
    catalogReady,
    applySignupReviewBowlers,
    skipPaperRefillRef,
    resetBowlers,
    getAllEventStatus, // Expose the all event status function
  };
};
